import { ContactType, type IUser } from '@condo/domain';
import { UserPlus, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Badge } from '@/shared/components/ui/badge';
import { Label } from '@/shared/components/ui/label';
import { ReneoSelect } from '@/shared/components/ui/ReneoSelect';
import { RamAvatar } from '@/shared/components/RamAvatar';
import { cn } from '@/shared/utils';
import { getContactAvatarThumbUrl, resolvePersonImageSrcFromUser } from '../lib/contactAvatarUrl';
import { useContactsByIds, useExternalContactSearch, useSearchContacts } from '../model/contacts.hook';
import { formatContactName } from '../lib/contactHelpers';
import type { Contact } from '../model';
import { getContactTypeBadgeVariant } from '../lib/contactTypeBadge';
import { useDebounce } from '@/shared/hooks/useDebounce';

type ContactOption = Contact & { label: string; value: string };

function hasEmail(c: Contact): boolean {
    return Boolean(c.email?.trim());
}

interface ContactSelectorProps {
    contacts: Contact[];
    selectedContacts: Contact[];
    onSelectionChange: (contacts: Contact[]) => void;
    defaultContactIds?: number[];
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    searchMode?: boolean;
    /** When true, allow multiple selections. When false, single select only. Default: false */
    multi?: boolean;
    /** Restrict search to these contact types (e.g. [ContactType.User] for internal approvers) */
    contactTypes?: ContactType[];
    /** Max number of selections (only applies when multi is true) */
    maxSelections?: number;
    /** Custom renderer for selected value chips. Defaults to "Fullname · email" if not provided. */
    labelRenderer?: (option: ContactOption) => React.ReactNode;
    /** When true, contacts without an email are hidden from the options list. */
    hideOptionsWithoutEmail?: boolean;
    /** When true, use typed search. When false or no contact types are provided, use external search. Default: false */
    typedSearch?: boolean;
    /** When false, hide contact-type badges in dropdown options (e.g. list already filtered to one type). Default: true */
    showContactTypeBadge?: boolean;
    /** When true, prefix options/selected value with `ID:{contactId}` (Cockpit CompanyContactQuickSearch parity). Default: false */
    showEntityId?: boolean;
}

function toContactOption(c: Contact): ContactOption {
    return { ...c, label: formatContactName(c), value: String(c.contactId) };
}

/** Full name line for the selected-value chip (dropdown options use {@link formatContactName}). */
function contactPrimaryDisplayName(c: Contact): string {
    const fromFull = c.fullname?.trim();
    if (fromFull) return fromFull;
    const fromParts = `${c.firstname ?? ''} ${c.lastname ?? ''}`.trim();
    if (fromParts) return fromParts;
    const fromEmail = c.email?.trim();
    if (fromEmail) return fromEmail;
    return 'Unknown';
}

function preferNonEmptyString(a?: string | null, b?: string | null): string | undefined {
    const ta = a?.trim();
    const tb = b?.trim();
    if (ta) return a!;
    if (tb) return b!;
    return a ?? b ?? undefined;
}

/**
 * `useContactsByIds` (and similar) may return sparse contacts (e.g. only `contactId`) while the options list
 * was built from a richer source (e.g. company employment). Merge so name/email fields are not lost.
 */
function mergeContactPreferRicher(a?: Contact, b?: Contact): Contact | undefined {
    if (!a && !b) return undefined;
    if (!a) return b;
    if (!b) return a;
    return {
        ...b,
        ...a,
        fullname: preferNonEmptyString(a.fullname, b.fullname),
        firstname: preferNonEmptyString(a.firstname, b.firstname),
        lastname: preferNonEmptyString(a.lastname, b.lastname),
        email: preferNonEmptyString(a.email, b.email),
        userId: a.userId ?? b.userId,
        contactType: a.contactType ?? b.contactType,
        avatarMediaFile: a.avatarMediaFile ?? b.avatarMediaFile,
    };
}

/**
 * react-select often passes a stripped `data` object (label/value only) into `SingleValue` / `MultiValueLabel`.
 * Re-bind to the full contact from parent state or the current options list so `userId`, `avatarMediaFile`, etc. exist.
 */
function mergeContactOptionForDisplay(raw: ContactOption, selectedContacts: Contact[], optionsList: ContactOption[]): ContactOption {
    const id = raw.contactId ?? Number.parseInt(String(raw.value), 10);
    if (!Number.isFinite(id)) return raw;

    const fromSelected = selectedContacts.find(c => c.contactId === id);
    const fromOptions = optionsList.find(o => o.contactId === id);

    const merged = mergeContactPreferRicher(fromSelected, fromOptions);
    if (merged) return toContactOption(merged);

    if (fromOptions) return fromOptions;
    if (fromSelected) return toContactOption(fromSelected);

    return raw;
}

function resolveContactOptionImageSrc(option: ContactOption, getUser: (id: number | string) => IUser | undefined): string | undefined {
    if (option.userId) {
        const fromUid = resolvePersonImageSrcFromUser(getUser(option.userId));
        if (fromUid) return fromUid;
    }

    const contactId = option.contactId ?? Number.parseInt(String(option.value), 10);
    if (Number.isFinite(contactId)) {
        const fromBootstrapUser = resolvePersonImageSrcFromUser(getUser(contactId));
        if (fromBootstrapUser) return fromBootstrapUser;
    }

    return getContactAvatarThumbUrl(option.avatarMediaFile);
}

/** Selected value + multi chips: avatar (photo or initials) for every contact, not only `ContactType.User`. */
function ContactSelectorLabelRow({ option, showEntityId }: { option: ContactOption; showEntityId: boolean }) {
    const getUser = (_id: number | string) => undefined; // user resolution moved to caller via resolveUserImageSrc prop
    const imageSrc = resolveContactOptionImageSrc(option, getUser);
    const fromFields = contactPrimaryDisplayName(option);
    const primary = fromFields !== 'Unknown' ? fromFields : formatContactName(option);
    const fullnameForAvatar =
        option.fullname?.trim() || `${option.firstname ?? ''} ${option.lastname ?? ''}`.trim() || option.email || primary || String(option.contactId);
    const showEmailSuffix = Boolean(option.email?.trim() && primary !== option.email?.trim());
    const entityId = option.contactId;
    const title = showEntityId && entityId != null ? `ID:${entityId} ${primary}` : showEmailSuffix ? `${primary} · ${option.email}` : primary;

    return (
        <span className="flex min-w-0 max-w-full items-center gap-2" title={title}>
            <span className="shrink-0">
                <RamAvatar imageSrc={imageSrc} fullname={fullnameForAvatar} />
            </span>
            <span className="min-w-0 flex-1 overflow-hidden">
                {showEntityId && entityId != null ? (
                    <span className="block truncate font-mono text-sm">
                        <span className="font-semibold text-gray-700/75">ID:{entityId}</span>
                        <span className="ml-1 font-semibold text-sky-700">{primary}</span>
                    </span>
                ) : (
                    <>
                        <span className="block truncate text-sm font-medium text-slate-700">{primary}</span>
                        {showEmailSuffix && <span className="block truncate text-xs font-normal text-slate-500">{option.email}</span>}
                    </>
                )}
            </span>
        </span>
    );
}

/** Dropdown option row: avatar for every contact; type badge when not User (unless hidden). */
function ContactSelectorOptionRow({
    option,
    showContactTypeBadge,
    showEntityId,
}: {
    option: ContactOption;
    showContactTypeBadge: boolean;
    showEntityId: boolean;
}) {
    const getUser = (_id: number | string) => undefined; // user resolution moved to caller via resolveUserImageSrc prop
    const imageSrc = resolveContactOptionImageSrc(option, getUser);
    const isUser = option.contactType === ContactType.User;
    const fullnameForAvatar = option.fullname?.trim() || option.email || String(option.contactId);
    const displayName = formatContactName(option);
    const entityId = option.contactId;
    const title = showEntityId && entityId != null ? `ID:${entityId} ${displayName}` : option.email?.trim() ? `${displayName} · ${option.email}` : displayName;

    return (
        <div className="flex w-full min-w-0 items-start gap-2" title={title}>
            <span className="shrink-0">
                <RamAvatar imageSrc={imageSrc} fullname={fullnameForAvatar} />
            </span>
            <div className="min-w-0 flex-1">
                {showEntityId && entityId != null ? (
                    <div className="break-words font-mono text-sm">
                        <span className="font-semibold text-gray-700/75">ID:{entityId}</span>
                        <span className="ml-1 font-semibold text-sky-700">{displayName}</span>
                    </div>
                ) : (
                    <div className="break-words text-sm font-medium text-gray-900">{displayName}</div>
                )}
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                    <span className={option.email ? 'min-w-0 break-all text-xs text-gray-500' : 'text-xs italic text-red-500'}>
                        {option.email ?? 'No Email'}
                    </span>
                    {showContactTypeBadge && !isUser && option.contactType && (
                        <Badge variant={getContactTypeBadgeVariant(option.contactType)} className="shrink-0 px-2 py-0.5 text-xs">
                            {option.contactType}
                        </Badge>
                    )}
                </div>
            </div>
        </div>
    );
}

function getNoOptionsMessage(searchMode: boolean, inputValue: string, minChars: number): string {
    if (!searchMode) return 'No contacts found.';
    if (inputValue.trim().length < minChars) {
        return `Type at least ${minChars} character${minChars > 1 ? 's' : ''} to search...`;
    }
    return 'No contacts found.';
}

export function ContactSelector({
    contacts,
    selectedContacts,
    onSelectionChange,
    defaultContactIds,
    placeholder = 'Select contacts...',
    label,
    disabled = false,
    searchMode = false,
    multi = false,
    contactTypes,
    maxSelections,
    labelRenderer,
    hideOptionsWithoutEmail = false,
    typedSearch = false,
    showContactTypeBadge = true,
    showEntityId = false,
}: ContactSelectorProps) {
    const [inputValue, setInputValue] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const lastLoadedResultsRef = useRef<Contact[]>([]);
    /** Apply `defaultContactIds` once per id set. Re-applying whenever the selection is empty puts a removed chip straight back. */
    const appliedDefaultsKeyRef = useRef('');

    const debouncedInputValue = useDebounce(inputValue, 300);

    const defaultsKey = (defaultContactIds ?? []).join(',');
    const { data: defaultContacts } = useContactsByIds(defaultContactIds, !!defaultContactIds?.length && selectedContacts.length === 0);

    useEffect(() => {
        if (!defaultsKey || appliedDefaultsKeyRef.current === defaultsKey) return;
        // Selection already set (including after the user removes a chip on a remount) — do not seed again.
        if (selectedContacts.length > 0) {
            appliedDefaultsKeyRef.current = defaultsKey;
            return;
        }
        if (!defaultContacts?.length) return;
        appliedDefaultsKeyRef.current = defaultsKey;
        onSelectionChange(defaultContacts as Contact[]);
    }, [defaultsKey, defaultContacts, selectedContacts.length, onSelectionChange]);

    const useTypedSearch = typedSearch || (contactTypes != null && contactTypes.length > 0);

    const { data: typedSearchResults = [], isLoading: isTypedSearching } = useSearchContacts(debouncedInputValue.trim(), contactTypes ?? [], useTypedSearch);
    const { data: externalSearchResults = [], isLoading: isExternalSearching } = useExternalContactSearch(
        debouncedInputValue,
        searchMode && menuOpen && !useTypedSearch,
    );

    const searchResults = useTypedSearch ? typedSearchResults : externalSearchResults;
    const isSearching = useTypedSearch ? isTypedSearching : isExternalSearching;

    useEffect(() => {
        if (searchMode && searchResults.length > 0) {
            lastLoadedResultsRef.current = searchResults;
        }
    }, [searchMode, searchResults]);

    const minChars = useTypedSearch ? 1 : 2;

    const selectedIds = new Set(selectedContacts.map(c => c.contactId));
    const source = searchMode ? (debouncedInputValue.trim().length >= minChars ? searchResults : lastLoadedResultsRef.current) : contacts;
    const filteredSource = hideOptionsWithoutEmail ? source.filter(c => hasEmail(c) || selectedIds.has(c.contactId)) : source;
    const uniqueContacts = new Map<number, Contact>();
    [...selectedContacts, ...filteredSource].forEach(c => uniqueContacts.set(c.contactId, c));
    const options = Array.from(uniqueContacts.values()).map(toContactOption);

    const noOptionsMessage = getNoOptionsMessage(searchMode, debouncedInputValue, minChars);

    /** Match `FormField` layout: label row wrapper + `space-y-2` so controls align with `FormSelect` / `FormInput` in grid rows. */
    return (
        <div className="space-y-2">
            {label && (
                <div className="flex items-center gap-1.5">
                    <Label className={cn('flex items-center gap-1.5 text-gray-900')}>
                        {label.includes('External') ? (
                            <UserPlus className="h-4 w-4 shrink-0 text-muted-foreground" />
                        ) : (
                            <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                        {label}
                    </Label>
                </div>
            )}
            <ReneoSelect<ContactOption, boolean>
                options={options}
                value={multi ? selectedContacts.map(toContactOption) : selectedContacts[0] ? toContactOption(selectedContacts[0]) : null}
                onChange={v => {
                    if (multi) {
                        onSelectionChange((v ?? []) as Contact[]);
                    } else {
                        const single = v as ContactOption | null | undefined;
                        onSelectionChange(single ? [single as Contact] : []);
                    }
                }}
                emitObject
                multi={multi}
                maxSelections={multi ? maxSelections : undefined}
                placeholder={placeholder}
                disabled={disabled}
                isLoading={searchMode && isSearching}
                onInputChange={v => setInputValue(v)}
                onMenuOpen={() => setMenuOpen(true)}
                onMenuClose={() => setMenuOpen(false)}
                filterOption={searchMode ? () => true : undefined}
                noOptionsMessage={() => noOptionsMessage}
                labelRenderer={
                    labelRenderer ??
                    (opt => (
                        <ContactSelectorLabelRow
                            option={mergeContactOptionForDisplay(opt as ContactOption, selectedContacts, options)}
                            showEntityId={showEntityId}
                        />
                    ))
                }
                optionRenderer={opt => (
                    <ContactSelectorOptionRow option={opt as ContactOption} showContactTypeBadge={showContactTypeBadge} showEntityId={showEntityId} />
                )}
            />
        </div>
    );
}
